import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Copy, ArrowsClockwise, CheckCircle, Eye, EyeSlash, Lightning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntegrationsPage = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [form, setForm] = useState({
    meta_access_token: '',
    meta_page_id: '',
    meta_form_ids: '',
    auto_assign_to: '',
    default_status: 'New',
  });

  const baseHost = API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/integrations/config`, { withCredentials: true });
      setCfg(r.data);
      setForm((f) => ({
        ...f,
        meta_page_id: r.data.meta_page_id || '',
        meta_form_ids: r.data.meta_form_ids || '',
        auto_assign_to: r.data.auto_assign_to || '',
        default_status: r.data.default_status || 'New',
      }));
    } catch (e) {
      alert('Failed to load config: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Don't send blank token; backend treats empty string as "don't change"
      const payload = { ...form };
      if (!payload.meta_access_token) delete payload.meta_access_token;
      await axios.put(`${API_URL}/api/integrations/config`, payload, { withCredentials: true });
      setForm({ ...form, meta_access_token: '' });  // clear input after save
      await load();
      alert('Saved ✓');
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const clearToken = async () => {
    if (!window.confirm('Remove the saved Meta access token?')) return;
    await axios.put(`${API_URL}/api/integrations/config`, { meta_access_token: 'CLEAR' }, { withCredentials: true });
    await load();
  };

  const regenSecret = async () => {
    if (!window.confirm('Regenerate the webhook secret? Any existing FB/Google webhook config will need to be updated with the new secret.')) return;
    await axios.put(`${API_URL}/api/integrations/config`, { regenerate_webhook_secret: true }, { withCredentials: true });
    await load();
  };

  const syncMeta = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API_URL}/api/integrations/meta/sync`, {}, { withCredentials: true });
      alert(`Sync complete ✓\n\nFetched: ${r.data.fetched}\nNew leads: ${r.data.new}\nForms processed: ${r.data.forms?.length || 0}\n${r.data.errors?.length ? '\nErrors:\n' + r.data.errors.join('\n') : ''}`);
      await load();
    } catch (e) {
      alert('Sync failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    // tiny visual feedback would be nice; just alert briefly for now
  };

  if (loading || !cfg) {
    return <Card className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>Loading integrations…</Card>;
  }

  const webhookUrl = `${baseHost}/api/integrations/webhook/lead?secret=${cfg.webhook_secret}`;
  const metaWebhookUrl = `${baseHost}/api/integrations/webhook/meta`;

  return (
    <div data-testid="integrations-page" className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>
          <Lightning size={22} weight="fill" className="inline mr-1.5 mb-1" style={{ color: '#f59e0b' }} /> Lead Integrations
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
          Pull leads directly from Facebook / Instagram / Google Ads / Zapier into your Leads CRM.
        </p>
      </div>

      <Tabs defaultValue="meta" className="w-full">
        <TabsList className="grid grid-cols-3 max-w-md">
          <TabsTrigger value="meta" data-testid="tab-meta">📘 Meta (FB/IG)</TabsTrigger>
          <TabsTrigger value="webhook" data-testid="tab-webhook">🪝 Webhook (Google/Zapier)</TabsTrigger>
          <TabsTrigger value="defaults" data-testid="tab-defaults">⚙️ Defaults</TabsTrigger>
        </TabsList>

        {/* ============ META ============ */}
        <TabsContent value="meta" className="space-y-4 mt-4">
          <Card className="p-5">
            <h3 className="text-base font-bold mb-1">Meta Lead Ads — Direct API</h3>
            <p className="text-xs text-gray-600 mb-4">Connect once with a Page Access Token. Then click "Sync Now" anytime to import all Lead Ad submissions into your CRM.</p>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Page Access Token *</Label>
                <div className="flex gap-2 items-center mt-1">
                  <Input
                    type={showToken ? 'text' : 'password'}
                    placeholder={cfg.meta_access_token_set ? cfg.meta_access_token_masked : 'Paste long-lived Page Access Token…'}
                    value={form.meta_access_token}
                    onChange={(e) => setForm({ ...form, meta_access_token: e.target.value })}
                    data-testid="meta-token-input"
                    className="font-mono text-xs"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowToken(!showToken)} title={showToken ? 'Hide' : 'Show'}>
                    {showToken ? <EyeSlash size={14} /> : <Eye size={14} />}
                  </Button>
                  {cfg.meta_access_token_set && (
                    <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={clearToken} data-testid="clear-meta-token-btn">Remove</Button>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Required permissions: <code>leads_retrieval, pages_show_list, pages_read_engagement</code>. Leave blank to keep current token.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Page ID *</Label>
                  <Input value={form.meta_page_id} onChange={(e) => setForm({ ...form, meta_page_id: e.target.value })} placeholder="123456789012345" data-testid="meta-page-id-input" />
                </div>
                <div>
                  <Label className="text-xs">Form IDs (optional)</Label>
                  <Input value={form.meta_form_ids} onChange={(e) => setForm({ ...form, meta_form_ids: e.target.value })} placeholder="comma-separated; blank = all forms" data-testid="meta-form-ids-input" />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button onClick={save} disabled={saving} data-testid="save-meta-btn">
                  {saving ? 'Saving…' : 'Save Config'}
                </Button>
                <Button onClick={syncMeta} disabled={syncing || !cfg.meta_access_token_set || !cfg.meta_page_id} variant="outline" data-testid="sync-meta-btn">
                  <ArrowsClockwise size={14} className="mr-1.5" /> {syncing ? 'Syncing…' : 'Sync Now'}
                </Button>
                {cfg.last_meta_sync_at && (
                  <span className="text-[11px] text-gray-500 ml-2">
                    Last sync: {new Date(cfg.last_meta_sync_at).toLocaleString()}{' '}
                    ({cfg.last_meta_sync_summary?.new ?? 0} new of {cfg.last_meta_sync_summary?.fetched ?? 0})
                  </span>
                )}
              </div>
            </div>

            <details className="mt-5 text-xs">
              <summary className="cursor-pointer font-semibold text-gray-700">How to get a Page Access Token (5 min)</summary>
              <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-gray-700">
                <li>Go to <a href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noreferrer" className="text-blue-600 underline">Graph API Explorer</a></li>
                <li>Top-right → select your <strong>App</strong> (create one if needed)</li>
                <li>"User Token" → switch to <strong>Page Access Token</strong> → pick your Page</li>
                <li>"Add Permissions" → check: <code>leads_retrieval</code>, <code>pages_show_list</code>, <code>pages_read_engagement</code>, <code>pages_manage_metadata</code></li>
                <li>Click <strong>Generate Access Token</strong> → copy</li>
                <li>To make it long-lived (60 days+), exchange via:{' '}
                  <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">GET /oauth/access_token?grant_type=fb_exchange_token&client_id=APP_ID&client_secret=APP_SECRET&fb_exchange_token=SHORT_TOKEN</code>
                </li>
                <li>Paste the long token above + your Page ID, click Save, then Sync Now</li>
              </ol>
            </details>
          </Card>

          <Card className="p-5">
            <h3 className="text-base font-bold mb-1">Realtime Webhook (optional)</h3>
            <p className="text-xs text-gray-600 mb-3">Instead of clicking "Sync Now", configure Meta to push new leads to your server instantly:</p>
            <div className="bg-gray-50 border rounded-md p-3 space-y-2 font-mono text-xs">
              <div><span className="text-gray-500">Callback URL:</span> <code data-testid="meta-webhook-url">{metaWebhookUrl}</code>
                <Button size="sm" variant="ghost" className="ml-2 h-6 px-2" onClick={() => copyToClipboard(metaWebhookUrl)}><Copy size={12} /></Button>
              </div>
              <div><span className="text-gray-500">Verify Token:</span> <code>{cfg.webhook_secret}</code>
                <Button size="sm" variant="ghost" className="ml-2 h-6 px-2" onClick={() => copyToClipboard(cfg.webhook_secret)}><Copy size={12} /></Button>
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">In Meta App Dashboard → Webhooks → Subscribe to Page → "leadgen" field. Paste the URL + Verify Token above.</p>
          </Card>
        </TabsContent>

        {/* ============ GENERIC WEBHOOK ============ */}
        <TabsContent value="webhook" className="space-y-4 mt-4">
          <Card className="p-5">
            <h3 className="text-base font-bold mb-1">Webhook URL (for Google Ads, Zapier, Make, n8n, custom forms)</h3>
            <p className="text-xs text-gray-600 mb-3">
              Any tool that can POST JSON to this URL can drop leads directly into your CRM. The secret is in the URL — keep it private.
            </p>

            <div className="bg-gray-50 border rounded-md p-3 mb-3 break-all font-mono text-xs flex items-start justify-between gap-2">
              <code data-testid="webhook-url">{webhookUrl}</code>
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(webhookUrl)} data-testid="copy-webhook-btn">
                <Copy size={12} className="mr-1" /> Copy
              </Button>
            </div>

            <Button size="sm" variant="outline" className="text-red-600" onClick={regenSecret} data-testid="regen-secret-btn">
              <ArrowsClockwise size={14} className="mr-1.5" /> Regenerate Secret
            </Button>

            <details className="mt-5 text-xs" open>
              <summary className="cursor-pointer font-semibold text-gray-700">Expected payload (JSON)</summary>
              <pre className="bg-gray-50 border rounded p-3 mt-2 overflow-x-auto text-[11px]">{`{
  "name": "Mr. Anand",           // OR  "first_name" + "last_name"  OR  "full_name"
  "phone": "9876543210",
  "email": "anand@example.com",
  "company": "Acme Pvt Ltd",     // optional
  "source": "Website",           // Call/Landline/Website/Social/Referral/Other
  "service_interested": "Website Development",
  "notes": "Came from Diwali campaign",
  "external_id": "google:lead_123"   // optional but recommended — prevents duplicates
}`}</pre>
            </details>

            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-semibold text-gray-700">Google Ads Lead Form Extensions — webhook setup</summary>
              <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-gray-700">
                <li>Google Ads → Ads & Extensions → your Lead Form extension → <strong>Webhook integration</strong></li>
                <li>Paste the webhook URL above into "Webhook URL"</li>
                <li>Paste the secret (the <code>secret=...</code> part of the URL) into "Key" if Google asks for it</li>
                <li>Click <strong>Send test data</strong> — verify a "Test User" lead appears in /leads</li>
                <li>Save & enable</li>
              </ol>
              <p className="mt-2 text-[10px] text-gray-500">
                Google sends nested <code>user_column_data</code> — we auto-handle it.
              </p>
            </details>

            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-semibold text-gray-700">Zapier / Make.com / n8n setup</summary>
              <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-gray-700">
                <li>Create a new Zap (or Make scenario / n8n workflow)</li>
                <li>Trigger: Facebook Lead Ads OR Google Ads OR LinkedIn OR JustDial etc.</li>
                <li>Action: <strong>Webhook by Zapier → POST</strong></li>
                <li>URL: the webhook URL above</li>
                <li>Data: map the lead fields (name, phone, email, source) to the JSON schema above</li>
                <li>Test → enable</li>
              </ol>
            </details>

            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-semibold text-gray-700">Quick curl test</summary>
              <pre className="bg-gray-50 border rounded p-3 mt-2 overflow-x-auto text-[11px]">{`curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{"name":"Test Lead","phone":"9876543210","source":"Website"}'`}</pre>
            </details>
          </Card>
        </TabsContent>

        {/* ============ DEFAULTS ============ */}
        <TabsContent value="defaults" className="space-y-4 mt-4">
          <Card className="p-5">
            <h3 className="text-base font-bold mb-3">Defaults for imported leads</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Auto-assign to (optional)</Label>
                <Input value={form.auto_assign_to} onChange={(e) => setForm({ ...form, auto_assign_to: e.target.value })}
                  placeholder="e.g. Aksha" data-testid="auto-assign-input" />
                <p className="text-[10px] text-gray-500 mt-1">Name to set as Assigned-To for every imported lead. Leave blank for unassigned.</p>
              </div>
              <div>
                <Label className="text-xs">Default status</Label>
                <select value={form.default_status} onChange={(e) => setForm({ ...form, default_status: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="default-status-select">
                  <option>New</option>
                  <option>Contacted</option>
                  <option>Qualified</option>
                </select>
              </div>
            </div>
            <Button className="mt-4" onClick={save} disabled={saving} data-testid="save-defaults-btn">
              {saving ? 'Saving…' : <><CheckCircle size={14} weight="fill" className="mr-1.5" /> Save Defaults</>}
            </Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationsPage;
